import { useId, useState } from 'react';
import { Link } from 'wouter';
import { RESET_TOKEN_TTL_MINUTES } from '../../shared/constants.ts';
import { describeApiFailure } from '../api/http.ts';
import { useRequestReset } from '../auth/session.ts';
import { useDocumentTitle } from '../hooks/use-document-title.ts';
import { AuthBrand } from './AuthBrand.tsx';
import styles from './LoginPage.module.css';

// "Forgot password": ask for a username; the account's email address (if
// it has one) gets a one-time link. The answer never says whether the
// account exists. Where mail is not set up, the API says to ask an admin.

export function ForgotPage() {
  const usernameId = useId();
  const errorId = useId();
  const [username, setUsername] = useState('');
  const request = useRequestReset();
  useDocumentTitle('Forgot password');

  return (
    <main className={styles['page']}>
      <form
        className={styles['card']}
        aria-describedby={errorId}
        onSubmit={(event) => {
          event.preventDefault();
          request.mutate({ username });
        }}
      >
        <AuthBrand />
        {request.isSuccess ? (
          <p className={styles['lead']} role="status">
            If that account has an email address, a link is on its way. It works for{' '}
            {RESET_TOKEN_TTL_MINUTES} minutes.
          </p>
        ) : (
          <>
            <p className={styles['lead']}>
              Enter your username. The email address on the account gets a link to choose a new
              password.
            </p>
            <label className={styles['label']} htmlFor={usernameId}>
              Username
            </label>
            <input
              id={usernameId}
              className={styles['input']}
              name="username"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              value={username}
              onChange={(event) => {
                setUsername(event.target.value);
              }}
            />
            <div id={errorId} role="alert" className={styles['error']}>
              {request.isError ? describeApiFailure(request.error) : null}
            </div>
            <button className={styles['button']} type="submit" disabled={request.isPending}>
              {request.isPending ? 'Sending…' : 'Send the link'}
            </button>
          </>
        )}
        <p className={styles['foot']}>
          <Link href="/login" className={styles['link']}>
            Back to sign in
          </Link>
        </p>
      </form>
    </main>
  );
}
